import React, {
  useContext,
  createContext,
  useState,
  useEffect,
  useMemo,
  PropsWithChildren,
} from 'react';

/* Providers */
import { useShopContext } from '../../Shops/shared/__providers__/ShopContext';
import { useStoreContext } from './StoreContext';
import { useBuyerContext } from './BuyerProvider';
import { useProductContext } from './ProductContext';
import { useLoadingContext } from './LoadingProvider';
// import { usePromoCodeContext } from './PromoCodeProvider';
import { useAnalyticsHandlers } from '../../../analytics/__providers__/AnalyticsHandlersProvider';
import { usePaymentTypeContext } from './PaymentTypeContext';
import { usePurchaseStatusProvider } from '../../Shops/shared/__providers__/PurchaseStatusProvider';

/* Interfaces */
import {
  Payload,
  PremiumPayload,
  TipPurchasePayload,
} from '../interfaces/Payload';
import { TipProduct } from '../../Shops/Tip/interfaces/TipProduct';
import { PremiumProduct } from '../../Shops/Premium/interfaces/PremiumProduct';
import { PaymentProcessor } from '../interfaces/PaymentProcessor';
import { StoreTypesReversed } from '../interfaces/store_types';
import { PixPaymentProcessor } from '../interfaces/PixPaymentProcessor';
import { StripePaymentProcessor } from '../interfaces/StripePaymentProcessor';
import { PaypalPaymentProcessor } from '../interfaces/PaypalPaymentProcessor';
import { TicketPaymentProcessor } from '../interfaces/TicketPaymentProcessor';
import { PaymentType, PaymentTypesReversed } from '../interfaces/payment_types';

/* Redux */
import { useDispatch } from 'react-redux';

interface PaymentProviderContent {
  paymentIntent: () => Promise<any>;
  verifyPayment: (params?: any) => Promise<any>;
  processor: PaymentProcessor | null;

  /* PIX related states */
  isPendingModalOpen: boolean;
  setIsPendingModalOpen: (opener: boolean) => void;
  purchaseID: string | null;
  linkAuthorize: string | null;
}

export const PaymentContent = createContext<PaymentProviderContent>({
  paymentIntent: () => Promise.resolve(() => ({})),
  verifyPayment: () => Promise.resolve(() => ({})),
  processor: null,

  /* PIX related states */
  isPendingModalOpen: false,
  setIsPendingModalOpen: () => {},
  purchaseID: null,
  linkAuthorize: null,
});

export const PaymentProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const { shop } = useShopContext();
  const { store } = useStoreContext();
  const { product } = useProductContext();
  // const {
  //   states: { promoCodeID },
  // } = usePromoCodeContext();
  const analytics = useAnalyticsHandlers();
  const { selectedPayment, paymentHandler, options } = usePaymentTypeContext();
  const { setLoading } = useLoadingContext();
  const {
    states: { buyer },
  } = useBuyerContext();

  const { setPurchaseStatus } = usePurchaseStatusProvider();

  /* PIX related states */
  const [isPendingModalOpen, setIsPendingModalOpen] = useState<boolean>(false);
  const [purchaseID, setPurchaseID] = useState<string | null>(null);
  const [linkAuthorize, setLinkAuthorize] = useState<string | null>(null);

  const payload: Payload = useMemo(() => {
    switch (store) {
      case StoreTypesReversed.Premium:
        return new PremiumPayload(
          (shop!.selectedProduct as PremiumProduct).plan.id,
          selectedPayment,
          buyer,
          // promoCodeID,
        );
      case StoreTypesReversed.TipPurchase:
        return new TipPurchasePayload(
          (shop!.selectedProduct as TipProduct).tip.id,
          selectedPayment,
          buyer,
        );
      default:
        return new PremiumPayload(
          (shop!.selectedProduct as PremiumProduct).plan.id,
          selectedPayment,
          buyer,
          // promoCodeID,
        );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayment, shop!.selectedProduct, buyer]);

  const getProcessor = (
    selectedPayment: PaymentType | undefined,
  ): PaymentProcessor => {
    switch (selectedPayment) {
      case PaymentTypesReversed.Ticket:
        return new TicketPaymentProcessor(
          dispatch,
          setLoading,
          analytics!.client,
          setPurchaseStatus,
          product.ID,
          product.unlockableID,
        );
      case PaymentTypesReversed.Stripe:
        return new StripePaymentProcessor(
          payload,
          shop!,
          dispatch,
          setLoading,
          analytics!.client,
          setPurchaseStatus,
          paymentHandler,
          options,
        );
      case PaymentTypesReversed.Paypal:
        return new PaypalPaymentProcessor(
          payload,
          dispatch,
          setLoading,
          analytics!.client,
          setPurchaseStatus,
        );
      case PaymentTypesReversed.Pix:
        return new PixPaymentProcessor(
          payload,
          dispatch,
          setLoading,
          analytics!.client,
          setPurchaseStatus,
          setIsPendingModalOpen,
          setPurchaseID,
          setLinkAuthorize,
        );
      default:
        return new StripePaymentProcessor(
          payload,
          shop!,
          dispatch,
          setLoading,
          analytics!.client,
          setPurchaseStatus,
          paymentHandler,
          options,
        );
    }
  };
  let processor: PaymentProcessor = getProcessor(selectedPayment);

  useEffect(() => {
    processor = getProcessor(selectedPayment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayment, payload]);

  const paymentIntent = async (): Promise<any> => {
    const response = await processor.processPayment(store);

    return response;
  };

  const verifyPayment = async (params?: any): Promise<any> => {
    const response = await processor.finishPayment(store, params);

    return response;
  };

  const value = {
    paymentIntent,
    verifyPayment,
    processor,

    /* PIX related states */
    isPendingModalOpen,
    setIsPendingModalOpen,
    purchaseID,
    linkAuthorize,
  };

  return (
    <PaymentContent.Provider value={value}>{children}</PaymentContent.Provider>
  );
};

export const usePaymentProvider = () => useContext(PaymentContent);
